import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import SignatureUpload from "@/components/feature/SignatureUpload";

export default function PerfilPage() {
  const { user, refreshUser } = useAuth();
  const [fullName, setFullName] = useState("");
  const [company, setCompany] = useState("");
  const [phone, setPhone] = useState("");
  const [position, setPosition] = useState("");
  const [profileMsg, setProfileMsg] = useState("");
  const [profileErr, setProfileErr] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);

  const [current, setCurrent] = useState("");
  const [newPass, setNewPass] = useState("");
  const [confirm, setConfirm] = useState("");
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user) return;
    setFullName(user.full_name || "");
    setCompany(user.company || "");
    setPhone(user.phone || "");
    setPosition(user.position || "");
  }, [user]);

  const handleProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setProfileErr("");
    setProfileMsg("");
    if (!fullName.trim()) {
      setProfileErr("El nombre es obligatorio");
      return;
    }
    setSavingProfile(true);
    try {
      await api.updateProfile({
        full_name: fullName.trim(),
        company: company.trim(),
        phone: phone.trim(),
        position: position.trim(),
      });
      setProfileMsg("Perfil actualizado correctamente");
      await refreshUser();
    } catch (e) {
      setProfileErr(e instanceof Error ? e.message : "Error al guardar");
    }
    setSavingProfile(false);
  };

  const handlePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr("");
    setMsg("");
    if (newPass !== confirm) {
      setErr("Las contraseñas no coinciden");
      return;
    }
    setLoading(true);
    try {
      await api.changePassword(current, newPass);
      setMsg("Contraseña actualizada");
      setCurrent("");
      setNewPass("");
      setConfirm("");
      await refreshUser();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Error");
    }
    setLoading(false);
  };

  if (!user) return null;

  const showSignature =
    user.role === "instructor" ||
    user.role === "evaluador" ||
    user.can_evaluate ||
    user.role === "admin" ||
    user.role === "super_admin";

  return (
    <div className="space-y-8 max-w-2xl mx-auto animate-[fadeIn_0.3s_ease-out]">
      <div className="text-center sm:text-left flex flex-col sm:flex-row items-center gap-6 mb-2">
        <div className="w-24 h-24 rounded-full bg-[var(--color-accent-oil)]/20 border-2 border-[var(--color-accent-oil)] flex items-center justify-center shadow-lg shadow-[var(--color-accent-oil)]/20">
          <span className="text-4xl font-bold text-[var(--color-accent-oil)]">
            {fullName.charAt(0).toUpperCase() || user.full_name.charAt(0).toUpperCase()}
          </span>
        </div>
        <div>
          <h1 className="text-3xl font-bold text-[var(--color-text-primary)]">{fullName || user.full_name}</h1>
          <p className="text-base text-[var(--color-text-secondary)] mt-1">{user.email}</p>
          <span className="inline-block mt-3 px-3 py-1 rounded-full text-xs font-semibold bg-[var(--color-accent)]/20 text-[var(--color-text-primary)] border border-[var(--color-border)] uppercase tracking-wide">
            {user.role}
          </span>
        </div>
      </div>

      <form onSubmit={handleProfile} className="card space-y-5">
        <div className="border-b border-[var(--color-border)] pb-4 mb-2">
          <h2 className="font-semibold text-lg flex items-center gap-2">
            <i className="ri-user-settings-line text-[var(--color-accent-oil)]"></i>
            Datos del perfil
          </h2>
          <p className="text-xs text-[var(--color-text-muted)] mt-1">
            Actualiza tu nombre, empresa, teléfono y cargo.
          </p>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1">Nombre completo</label>
            <input
              className="input-field"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              required
              placeholder="Tu nombre"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1">Empresa</label>
              <input
                className="input-field"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                placeholder="Nombre de la empresa"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1">Teléfono</label>
              <input
                className="input-field"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="Número de contacto"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1">Cargo</label>
            <input
              className="input-field"
              value={position}
              onChange={(e) => setPosition(e.target.value)}
              placeholder="Tu cargo o puesto"
            />
          </div>
        </div>

        {profileMsg && (
          <div className="bg-green-500/10 border border-green-500/20 text-green-400 p-3 rounded-xl text-sm flex items-center gap-2">
            <i className="ri-check-line"></i> {profileMsg}
          </div>
        )}
        {profileErr && (
          <div className="bg-red-500/10 border border-red-500/20 text-red-400 p-3 rounded-xl text-sm flex items-center gap-2">
            <i className="ri-error-warning-line"></i> {profileErr}
          </div>
        )}

        <div className="pt-2">
          <button type="submit" disabled={savingProfile} className="btn-primary w-full sm:w-auto px-8 cursor-pointer">
            {savingProfile ? "Guardando..." : "Guardar perfil"}
          </button>
        </div>
      </form>

      {showSignature && (
        <div className="card">
          <h2 className="font-semibold text-lg flex items-center gap-2 mb-4">
            <i className="ri-pen-nib-line text-[var(--color-accent-oil)]"></i>
            Firma Digital
          </h2>
          <SignatureUpload />
        </div>
      )}

      <form onSubmit={handlePassword} className="card space-y-5">
        <div className="border-b border-[var(--color-border)] pb-4 mb-2">
          <h2 className="font-semibold text-lg flex items-center gap-2">
            <i className="ri-lock-password-line text-[var(--color-accent-oil)]"></i>
            Seguridad de la cuenta
          </h2>
          <p className="text-xs text-[var(--color-text-muted)] mt-1">
            Actualiza tu contraseña para mantener tu cuenta segura.
          </p>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1">Contraseña actual</label>
            <input type="password" className="input-field" placeholder="Ingresa tu contraseña actual" value={current} onChange={(e) => setCurrent(e.target.value)} required />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1">Nueva contraseña</label>
              <input type="password" className="input-field" placeholder="Mínimo 6 caracteres" value={newPass} onChange={(e) => setNewPass(e.target.value)} required minLength={6} />
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1">Confirmar nueva</label>
              <input type="password" className="input-field" placeholder="Repite la nueva contraseña" value={confirm} onChange={(e) => setConfirm(e.target.value)} required />
            </div>
          </div>
        </div>

        {msg && (
          <div className="bg-green-500/10 border border-green-500/20 text-green-400 p-3 rounded-xl text-sm flex items-center gap-2">
            <i className="ri-check-line"></i> {msg}
          </div>
        )}
        {err && (
          <div className="bg-red-500/10 border border-red-500/20 text-red-400 p-3 rounded-xl text-sm flex items-center gap-2">
            <i className="ri-error-warning-line"></i> {err}
          </div>
        )}

        <div className="pt-2">
          <button type="submit" disabled={loading} className="btn-primary w-full sm:w-auto px-8 cursor-pointer shadow-lg shadow-[var(--color-accent-oil)]/20">
            {loading ? "Guardando..." : "Actualizar contraseña"}
          </button>
        </div>
      </form>
    </div>
  );
}
